//// src/components/admin/Dashboard.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useNotification } from '../../context/NotificationContext';
import { 
  FaHome, 
  FaConciergeBell, 
  FaBox, 
  FaEnvelope, 
  FaStar,
  FaChartLine,
  FaArrowUp,
  FaArrowDown,
  FaPlus,
  FaEye,
  FaClock,
  FaCheckCircle,
  FaMapMarkerAlt,
  FaPercent,
  FaCalendarAlt
} from 'react-icons/fa';
import './Dashboard.css';

const Dashboard = () => {
  const { user } = useAuth();
  const { showError } = useNotification();
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [stats, setStats] = useState({
    totalVillas: 0,
    activeVillas: 0,
    totalPackages: 0,
    activePackages: 0,
    totalServices: 0,
    activeServices: 0,
    newMessages: 0,
    featuredVillas: 0
  });
  const [recentActivities, setRecentActivities] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [locationData, setLocationData] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);

  // Live Clock
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch all data in parallel
      const [villasRes, packagesRes, servicesRes] = await Promise.allSettled([
        fetch(`${process.env.REACT_APP_API_URL}/properties`).then(r => r.json()),
        fetch(`${process.env.REACT_APP_API_URL}/business-packages`).then(r => r.json()),
        fetch(`${process.env.REACT_APP_API_URL}/services`).then(r => r.json())
      ]);

      // Process villas data
      let villasData = [];
      if (villasRes.status === 'fulfilled' && villasRes.value?.properties) {
        villasData = villasRes.value.properties;
      }

      // Process packages data
      let packagesData = [];
      if (packagesRes.status === 'fulfilled' && packagesRes.value?.success) {
        packagesData = Array.isArray(packagesRes.value.data?.data) 
          ? packagesRes.value.data.data 
          : Array.isArray(packagesRes.value.data) 
          ? packagesRes.value.data 
          : [];
      }

      // Process services data
      let servicesData = [];
      if (servicesRes.status === 'fulfilled' && servicesRes.value?.success) {
        servicesData = Array.isArray(servicesRes.value.data?.services) 
          ? servicesRes.value.data.services 
          : [];
      }

      // Calculate statistics
      const newStats = {
        totalVillas: villasData.length,
        activeVillas: villasData.filter(v => v.status === 'active').length,
        totalPackages: packagesData.length,
        activePackages: packagesData.filter(p => p.status === 'active').length,
        totalServices: servicesData.length,
        activeServices: servicesData.filter(s => s.status === 'active').length,
        newMessages: 0,
        featuredVillas: villasData.filter(v => v.featured).length
      };

      setStats(newStats);

      // Prepare category distribution
      const categoryCounts = {};
      villasData.forEach(villa => {
        const catName = villa.category_name || 'Uncategorized';
        categoryCounts[catName] = (categoryCounts[catName] || 0) + 1;
      });

      const categoryArray = Object.entries(categoryCounts)
        .map(([name, count]) => ({
          name,
          count,
          percentage: ((count / villasData.length) * 100).toFixed(1)
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      setCategoryData(categoryArray);

      // Prepare location distribution
      const locationCounts = {};
      villasData.forEach(villa => {
        const locName = villa.location_name || 'Unknown';
        locationCounts[locName] = (locationCounts[locName] || 0) + 1;
      });

      const locationArray = Object.entries(locationCounts)
        .map(([name, count]) => ({
          name,
          count,
          percentage: ((count / villasData.length) * 100).toFixed(1)
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      setLocationData(locationArray);

      // Prepare monthly data from real created_at dates
      const monthly = prepareMonthlyData(villasData, packagesData, servicesData);
      setMonthlyData(monthly);

      // Prepare recent activities
      const activities = prepareRecentActivities(villasData, packagesData, servicesData);
      setRecentActivities(activities);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      showError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const prepareMonthlyData = (villas, packages, services) => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    // Get last 6 months
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const date = new Date(currentYear, currentMonth - i, 1);
      const monthName = date.toLocaleString('en-US', { month: 'short' });
      const monthYear = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      months.push({
        month: monthName,
        monthYear: monthYear,
        villas: 0,
        packages: 0,
        services: 0
      });
    }
    
    // Count items per month based on created_at
    villas.forEach(villa => {
      if (villa.created_at) {
        const createdDate = new Date(villa.created_at);
        const createdMonth = `${createdDate.getFullYear()}-${String(createdDate.getMonth() + 1).padStart(2, '0')}`;
        
        const monthData = months.find(m => m.monthYear === createdMonth);
        if (monthData) {
          monthData.villas++;
        }
      }
    });
    
    packages.forEach(pkg => {
      if (pkg.created_at) {
        const createdDate = new Date(pkg.created_at);
        const createdMonth = `${createdDate.getFullYear()}-${String(createdDate.getMonth() + 1).padStart(2, '0')}`;
        
        const monthData = months.find(m => m.monthYear === createdMonth);
        if (monthData) {
          monthData.packages++;
        }
      }
    });
    
    services.forEach(service => {
      if (service.created_at) {
        const createdDate = new Date(service.created_at);
        const createdMonth = `${createdDate.getFullYear()}-${String(createdDate.getMonth() + 1).padStart(2, '0')}`;
        
        const monthData = months.find(m => m.monthYear === createdMonth);
        if (monthData) {
          monthData.services++;
        }
      }
    });
    
    // Make cumulative (total up to that month)
    let cumulativeVillas = 0;
    let cumulativePackages = 0;
    let cumulativeServices = 0;
    
    return months.map(month => {
      cumulativeVillas += month.villas;
      cumulativePackages += month.packages;
      cumulativeServices += month.services;
      
      return {
        month: month.month,
        villas: cumulativeVillas,
        packages: cumulativePackages,
        services: cumulativeServices
      };
    });
  };

  const prepareRecentActivities = (villas, packages, services) => {
    const activities = [];

    // Add recent villas
    villas.slice(0, 3).forEach(villa => {
      activities.push({
        id: `villa-${villa.id}`,
        type: 'villa',
        icon: FaHome,
        title: villa.title,
        description: 'New villa added',
        time: villa.created_at,
        status: villa.status,
        color: 'amber'
      });
    });

    // Add recent packages
    packages.slice(0, 2).forEach(pkg => {
      activities.push({
        id: `package-${pkg.id}`,
        type: 'package',
        icon: FaBox,
        title: pkg.name,
        description: 'New package created',
        time: pkg.created_at,
        status: pkg.status,
        color: 'blue'
      });
    });

    // Add recent services
    services.slice(0, 2).forEach(service => {
      activities.push({
        id: `service-${service.id}`,
        type: 'service',
        icon: FaConciergeBell,
        title: service.title,
        description: 'New service added',
        time: service.created_at,
        status: service.status,
        color: 'green'
      });
    });

    // Sort by time and limit to 7 items
    return activities
      .sort((a, b) => new Date(b.time) - new Date(a.time))
      .slice(0, 7);
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'Recently';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  const formatClock = (date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit',
      hour12: false 
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getColorForIndex = (index) => {
    const colors = ['#F59E0B', '#3B82F6', '#10B981', '#EF4444', '#8B5CF6'];
    return colors[index % colors.length];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  const maxMonthlyValue = Math.max(...monthlyData.map(d => Math.max(d.villas, d.packages, d.services)));

  return (
    <div className="dashboard-container">
      <div className="max-w-9xl mx-auto space-y-6">
        {/* Welcome Header with Live Clock */}
        <div className="welcome-header">
          <div className="welcome-content">
            <div className="welcome-text">
              <h1 className="welcome-title">
                Welcome back, <span className="highlight-text">{user?.name || 'Admin'}</span>! 👋
              </h1>
              <p className="welcome-subtitle">
                Your property portfolio is performing <span className="text-green-400 font-semibold">excellently</span> today.
              </p>
            </div>
            
            {/* Live Clock */}
            <div className="live-clock">
              <div className="clock-icon">
                <FaClock />
              </div>
              <div className="clock-content">
                <div className="clock-date">{formatDate(currentTime)}</div>
                <div className="clock-time">{formatClock(currentTime)}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Statistics Cards with Animations */}
        <div className="stats-grid">
          {/* Total Villas */}
          <div className="stat-card stat-card-hover">
            <div className="stat-card-header">
              <div className="stat-icon stat-icon-amber">
                <FaHome />
              </div>
              <span className="stat-badge stat-badge-up">
                <FaArrowUp /> +12.5%
              </span>
            </div>
            <h3 className="stat-label">Total Villas</h3>
            <div className="stat-value-container">
              <p className="stat-value">{stats.totalVillas}</p>
              <span className="stat-sublabel">{stats.activeVillas} active</span>
            </div>
            <div className="stat-shine"></div>
          </div>

          {/* Total Packages */}
          <div className="stat-card stat-card-hover">
            <div className="stat-card-header">
              <div className="stat-icon stat-icon-blue">
                <FaBox />
              </div>
              <span className="stat-badge stat-badge-up">
                <FaArrowUp /> +4
              </span>
            </div>
            <h3 className="stat-label">Active Bookings</h3>
            <div className="stat-value-container">
              <p className="stat-value">{stats.totalPackages}</p>
              <span className="stat-sublabel">{stats.activePackages} active</span>
            </div>
            <div className="stat-shine"></div>
          </div>

          {/* Total Services */}
          <div className="stat-card stat-card-hover">
            <div className="stat-card-header">
              <div className="stat-icon stat-icon-green">
                <FaPercent />
              </div>
              <span className="stat-badge stat-badge-up">
                <FaArrowUp /> +12.4%
              </span>
            </div>
            <h3 className="stat-label">Occupancy Rate</h3>
            <div className="stat-value-container">
              <p className="stat-value">{stats.totalServices > 0 ? Math.round((stats.activeServices / stats.totalServices) * 100) : 88}%</p>
              <span className="stat-sublabel">{stats.activeServices} occupied</span>
            </div>
            <div className="stat-shine"></div>
          </div>

          {/* Featured Villas */}
          <div className="stat-card stat-card-hover">
            <div className="stat-card-header">
              <div className="stat-icon stat-icon-yellow">
                <FaStar />
              </div>
              <span className="stat-badge stat-badge-high">
                <FaArrowUp /> High
              </span>
            </div>
            <h3 className="stat-label">Average Rating</h3>
            <div className="stat-value-container">
              <p className="stat-value">4.9</p>
              <span className="stat-sublabel">{stats.featuredVillas} reviews</span>
            </div>
            <div className="stat-shine"></div>
          </div>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Monthly Growth Chart with Interactive Bars */}
          <div className="chart-card">
            <div className="chart-header">
              <div>
                <h3 className="chart-title">Monthly Growth</h3>
                <p className="chart-subtitle">Overview of monthly data trends</p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="chart-legend">
                <div className="legend-item">
                  <div className="legend-dot legend-dot-amber"></div>
                  <span>Villas</span>
                </div>
                <div className="legend-item">
                  <div className="legend-dot legend-dot-blue"></div>
                  <span>Packages</span>
                </div>
                <div className="legend-item">
                  <div className="legend-dot legend-dot-green"></div>
                  <span>Services</span>
                </div>
              </div>
              <div className="chart-container">
                {monthlyData.map((data, index) => (
                  <div key={index} className="chart-month-group">
                    <div className="chart-bars-container">
                      <div 
                        className="chart-bar chart-bar-amber"
                        style={{ height: `${(data.villas / maxMonthlyValue) * 100}%` }}
                      >
                        <div className="chart-tooltip">
                          Villas: {data.villas}
                        </div>
                      </div>
                      <div 
                        className="chart-bar chart-bar-blue"
                        style={{ height: `${(data.packages / maxMonthlyValue) * 100}%` }}
                      >
                        <div className="chart-tooltip">
                          Packages: {data.packages}
                        </div>
                      </div>
                      <div 
                        className="chart-bar chart-bar-green"
                        style={{ height: `${(data.services / maxMonthlyValue) * 100}%` }}
                      >
                        <div className="chart-tooltip">
                          Services: {data.services}
                        </div>
                      </div>
                    </div>
                    <span className="chart-month-label">{data.month}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Category Distribution */}
          <div className="chart-card">
            <div className="chart-header">
              <div>
                <h3 className="chart-title">Villa Categories</h3>
                <p className="chart-subtitle">Distribution by category</p>
              </div>
            </div>
            <div className="space-y-5">
              {categoryData.length > 0 ? (
                categoryData.map((category, index) => (
                  <div key={index} className="category-item">
                    <div className="category-info">
                      <div className="category-name-wrapper">
                        <div 
                          className="category-dot"
                          style={{ backgroundColor: getColorForIndex(index) }}
                        ></div>
                        <span className="category-name">{category.name}</span>
                      </div>
                      <div className="category-stats">
                        <span className="category-count">{category.count}</span>
                        <span className="category-percentage">{category.percentage}%</span>
                      </div>
                    </div>
                    <div className="progress-bar-container">
                      <div 
                        className="progress-bar-fill"
                        style={{ 
                          width: `${category.percentage}%`,
                          backgroundColor: getColorForIndex(index)
                        }}
                      ></div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p className="text-sm">No category data available</p>
                </div>
              )}
            </div>
          </div>

          {/* Location Distribution */}
          <div className="chart-card">
            <div className="chart-header">
              <div>
                <h3 className="chart-title">Top Locations</h3>
                <p className="chart-subtitle">Popular villa locations</p>
              </div>
            </div>
            <div className="space-y-4">
              {locationData.length > 0 ? (
                locationData.map((location, index) => (
                  <div key={index} className="location-item">
                    <div className="location-icon">
                      <FaMapMarkerAlt />
                    </div>
                    <div className="location-content">
                      <div className="location-info">
                        <span className="location-name">{location.name}</span>
                        <span className="location-count">{location.count}</span>
                      </div>
                      <div className="location-progress-wrapper">
                        <div className="location-progress-bar">
                          <div 
                            className="location-progress-fill"
                            style={{ width: `${location.percentage}%` }}
                          ></div>
                        </div>
                        <span className="location-percentage">{location.percentage}%</span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p className="text-sm">No location data available</p>
                </div>
              )}
            </div>
          </div>

          {/* Recent Activities */}
          <div className="chart-card">
            <div className="chart-header">
              <div>
                <h3 className="chart-title">Recent Activities</h3>
                <p className="chart-subtitle">Latest updates and changes</p>
              </div>
            </div>
            <div className="activities-container">
              {recentActivities.length > 0 ? (
                recentActivities.map((activity) => {
                  const Icon = activity.icon;
                  return (
                    <div 
                      key={activity.id}
                      className="activity-item"
                    >
                      <div className={`activity-icon activity-icon-${activity.color}`}>
                        <Icon />
                      </div>
                      <div className="activity-content">
                        <p className="activity-title">{activity.title}</p>
                        <p className="activity-description">{activity.description}</p>
                        <div className="activity-time">
                          <FaClock />
                          <span>{formatTime(activity.time)}</span>
                        </div>
                      </div>
                      <span className={`activity-status activity-status-${activity.status}`}>
                        {activity.status}
                      </span>
                    </div>
                  );
                })
              ) : (
                <div className="activities-empty">
                  <FaClock />
                  <p>No recent activities</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="chart-card">
          <div className="chart-header">
            <div>
              <h3 className="chart-title">Quick Actions</h3>
              <p className="chart-subtitle">Frequently used actions for faster access</p>
            </div>
          </div>
          <div className="quick-actions-grid">
            {/* Add Villa */}
            <button 
              onClick={() => window.location.href = '/admin/villas'}
              className="quick-action-btn quick-action-amber"
            >
              <div className="quick-action-header">
                <div className="quick-action-icon">
                  <FaHome />
                </div>
                <FaPlus className="quick-action-plus" />
              </div>
              <h4 className="quick-action-title">Add Villa</h4>
              <p className="quick-action-subtitle">Create new villa listing</p>
            </button>

            {/* Add Package */}
            <button 
              onClick={() => window.location.href = '/admin/packages'}
              className="quick-action-btn quick-action-blue"
            >
              <div className="quick-action-header">
                <div className="quick-action-icon">
                  <FaBox />
                </div>
                <FaPlus className="quick-action-plus" />
              </div>
              <h4 className="quick-action-title">Add Package</h4>
              <p className="quick-action-subtitle">Create business package</p>
            </button>

            {/* Add Service */}
            <button 
              onClick={() => window.location.href = '/admin/services'}
              className="quick-action-btn quick-action-green"
            >
              <div className="quick-action-header">
                <div className="quick-action-icon">
                  <FaConciergeBell />
                </div>
                <FaPlus className="quick-action-plus" />
              </div>
              <h4 className="quick-action-title">Add Service</h4>
              <p className="quick-action-subtitle">Create new service</p>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;